import { Component, inject, ViewChild, ElementRef, AfterViewInit, OnDestroy, OnInit, effect } from '@angular/core';
import { AppStateService } from '../../services/app-state.service';
import { TokenService } from '../../services/token.service';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import * as echarts from 'echarts';

@Component({
  selector: 'app-swap-page',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './swap-page.component.html'
})
export class SwapPageComponent implements OnInit, AfterViewInit, OnDestroy {
  public state = inject(AppStateService);
  private route = inject(ActivatedRoute);
  private tokenService = inject(TokenService);
  
  public parseFloat = parseFloat;

  constructor() {
    effect(() => {
      // Trigger chart update when token changes
      this.state.fromToken();
      if (this.chartInstance) {
        setTimeout(() => this.updateChartData(), 0);
      }
    });
  }
  
  @ViewChild('chartContainer') chartContainer!: ElementRef;
  private chartInstance: echarts.ECharts | null = null;
  private resizeObserver: ResizeObserver | null = null;
  
  activeChartTimeframe = '24H';
  timeframes = ['1H', '24H', '1W', '1M', '1Y'];
  
  activeFilterTab = 'all';

  setFilterTab(tab: string) {
    this.activeFilterTab = tab;
    this.state.tokenModalFilter.set(tab);
  }

  toggleFavorite(event: Event, token: any) {
    event.stopPropagation();
    const isAdded = this.state.toggleFavoriteToken(token.symbol);
    if (isAdded) {
      this.state.triggerNotification(`${token.symbol} added to favorites`, true);
    } else {
      this.state.triggerNotification(`${token.symbol} removed from favorites`, true);
    }
  }

  getExplorerUrl(address: string | undefined): string {
    const network = this.state.selectedNetworkId();
    let baseUrl = 'https://etherscan.io';
    if (network === 'sepolia') baseUrl = 'https://sepolia.etherscan.io';
    else if (network === 'bnb') baseUrl = 'https://bscscan.com';
    else if (network === 'matic') baseUrl = 'https://polygonscan.com';

    return address ? `${baseUrl}/address/${address}` : baseUrl;
  }

  ngOnInit() {
    this.route.queryParams.subscribe(params => {
      if (params['from']) {
        let fromToken = this.state.tokens().find(t => t.symbol.toUpperCase() === params['from'].toUpperCase());
        if (!fromToken) {
           fromToken = { symbol: params['from'], name: params['from'] + ' Token', pairName: params['from'] + ' / USDT', rate: 100, change: 0, color: '#888888' };
        }
        this.state.fromToken.set(fromToken);
      }
      if (params['to']) {
        let toToken = this.state.tokens().find(t => t.symbol.toUpperCase() === params['to'].toUpperCase());
        if (!toToken) {
           toToken = { symbol: params['to'], name: params['to'] + ' Token', pairName: params['to'] + ' / USDT', rate: 100, change: 0, color: '#888888' };
        }
        this.state.toToken.set(toToken);
      }
      if (params['amount']) {
        this.state.fromAmount.set(params['amount']);
        if (this.state.fromToken() && this.state.toToken()) {
          setTimeout(() => this.state.calculateRoute(), 500); // Small delay to ensure state is ready
        }
      }
    });
  }

  ngAfterViewInit() {
    this.initChart();
    
    if (this.chartContainer?.nativeElement) {
      this.resizeObserver = new ResizeObserver(() => {
        this.chartInstance?.resize();
      });
      this.resizeObserver.observe(this.chartContainer.nativeElement);
    }
  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.chartInstance) {
      this.chartInstance.dispose();
    }
  }

  setTimeframe(tf: string) {
    this.activeChartTimeframe = tf;
    this.updateChartData();
  }

  private initChart() {
    if (!this.chartContainer?.nativeElement) return;
    
    this.chartInstance = echarts.init(this.chartContainer.nativeElement);
    this.updateChartData();
  }
  
  private currentChartRequestId = 0;

  private async updateChartData() {
    if (!this.chartInstance) return;
    
    const token = this.state.fromToken();
    if (!token || !token.address) return;
    
    const requestId = ++this.currentChartRequestId;
    this.chartInstance.showLoading('default', {
      text: '',
      color: '#20d489',
      textColor: '#fff',
      maskColor: 'rgba(0, 0, 0, 0.4)',
      zlevel: 0
    });
    
    let rawData: number[][] = [];
    
    try {
      const data = await this.tokenService.getTokenHistory(token.address, this.activeChartTimeframe, token.symbol);
      if (requestId !== this.currentChartRequestId) return; // Prevent race conditions
      
      if (data && data.status && data.data && data.data.length > 0) {
        rawData = data.data;
      }
    } catch(e) {
      if (requestId !== this.currentChartRequestId) return; // Prevent race conditions
      console.error("Failed to fetch chart data", e);
    }
    
    // If still no data, show a flat line at the current price
    if (!rawData || rawData.length === 0) {
      const baseVal = token.rate || 0;
      const now = Date.now();
      rawData = [
        [now - 3600000, baseVal],
        [now, baseVal]
      ];
    }

    if (requestId === this.currentChartRequestId) {
      this.chartInstance.hideLoading();
      this.renderChartOptions(token, rawData);
    }
  }

  private renderChartOptions(token: any, rawData: number[][]) {
    if (!this.chartInstance) return;

    // We have [timestamp, price] pairs in rawData
    const data = rawData.map(d => d[1]);
    
    let xAxisData: string[] = [];
    
    // Format X axis based on timeframe and actual timestamps
    xAxisData = rawData.map(d => {
       const date = new Date(d[0]);
       switch(this.activeChartTimeframe) {
         case '1H': 
           return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
         case '24H':
           return date.toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'});
         case '1W':
         case '1M':
           return date.toLocaleDateString([], {month: 'short', day: 'numeric'});
         case '1Y':
           return date.toLocaleDateString([], {month: 'short', year: 'numeric'});
         default:
           return date.toLocaleTimeString();
       }
    });

    const option: echarts.EChartsOption = {
      grid: {
        top: 10,
        bottom: 20,
        left: 45,
        right: 10
      },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(10, 10, 10, 0.9)',
        borderColor: 'rgba(255, 255, 255, 0.1)',
        textStyle: { color: '#fff' }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: xAxisData,
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: '#888',
          fontSize: 10
        }
      },
      yAxis: {
        type: 'value',
        scale: true,
        splitLine: {
          lineStyle: { color: 'rgba(255, 255, 255, 0.03)' }
        },
        axisLabel: {
          color: '#888',
          fontSize: 10,
          formatter: (value: number) => {
             if (value < 0.01) return `$${value.toFixed(4)}`;
             return `$${value.toLocaleString()}`;
          }
        }
      },
      series: [
        {
          data: data,
          type: 'line',
          smooth: true,
          symbol: 'none',
          lineStyle: {
            color: token.color === 'transparent' ? '#F5B73B' : (token.color || '#F5B73B'),
            width: 2
          },
          areaStyle: {
            color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
              { offset: 0, color: (token.color === 'transparent' ? '#F5B73B' : (token.color || '#F5B73B')) + '40' },
              { offset: 1, color: (token.color === 'transparent' ? '#F5B73B' : (token.color || '#F5B73B')) + '00' }
            ])
          }
        }
      ]
    };
    
    this.chartInstance.setOption(option);
  }
}
